import React, { useState, useMemo } from 'react';
import { 
  ChevronDownIcon, 
  ChevronUpIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { FAQS, FAQ_CATEGORIES, POPULAR_TAGS } from '../data';
import Seo from './common/Seo';

function FAQs({ customFaqs = [], standalone = false }) {
  const faqsToUse = customFaqs.length > 0 ? customFaqs : FAQS;
  const [openItems, setOpenItems] = useState({});
  const [activeCategory, setActiveCategory] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTag, setActiveTag] = useState('');

  // Calcular categorías dinámicamente basado en las FAQs
  const categories = useMemo(() => {
    return FAQ_CATEGORIES.map(cat => ({
      ...cat,
      count: cat.id === 'todos' 
        ? faqsToUse.length 
        : faqsToUse.filter(f => f.category === cat.id).length
    }));
  }, [faqsToUse]);

  // Filtrar FAQs por categoría, término de búsqueda y tag
  const filteredFaqs = useMemo(() => {
    return faqsToUse.filter(faq => {
      const matchesCategory = activeCategory === 'todos' || faq.category === activeCategory;
      const matchesSearch = searchTerm === '' || 
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTag = activeTag === '' || 
        (faq.tags && faq.tags.some(tag => tag.toLowerCase() === activeTag.toLowerCase()));
      
      return matchesCategory && matchesSearch && matchesTag;
    });
  }, [faqsToUse, activeCategory, searchTerm, activeTag]);

  // Abrir todas las FAQs
  const openAll = () => {
    const allOpen = {};
    filteredFaqs.forEach(faq => {
      allOpen[faq.id] = true;
    });
    setOpenItems(allOpen);
  };

  // Cerrar todas las FAQs
  const closeAll = () => {
    setOpenItems({});
  };

  const toggleFAQ = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleKeyDown = (e, id) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleFAQ(id);
    }
  };

  // Función para resaltar el texto de búsqueda
  const highlightText = (text, highlight) => {
    if (!highlight.trim()) return text;
    
    // Escapar caracteres especiales para regex
    const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedHighlight})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) 
        ? <mark key={index} className="bg-yellow-200 font-semibold not-italic">{part}</mark> 
        : part
    );
  };

  // Limpiar todos los filtros
  const clearFilters = () => {
    setSearchTerm('');
    setActiveCategory('todos');
    setActiveTag('');
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqsToUse.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section
      aria-labelledby="faq-heading"
      className="bg-gradient-to-br from-blue-50 to-blue-100 py-16 px-4 sm:px-6 lg:px-8 rounded-2xl shadow-lg my-12"
      id="preguntas-frecuentes"
    >
      {standalone && (
        <Seo
          title="Preguntas Frecuentes | Geriatría Serena"
          description="Resolvemos las dudas más comunes sobre horarios, precios, certificaciones y cobertura de nuestros servicios de enfermería y cuidado a domicilio en Guadalajara."
          canonical="https://geriatriaserena.com/faqs"
          jsonLd={faqSchema}
          meta={[
            { property: 'og:title', content: 'Preguntas Frecuentes | Geriatría Serena' },
            { property: 'og:description', content: 'Horarios, precios, certificaciones y cobertura de nuestros servicios de enfermería a domicilio en Guadalajara.' },
            { property: 'og:type', content: 'website' },
            { property: 'og:url', content: 'https://geriatriaserena.com/faqs' }
          ]}
        />
      )}
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          {standalone && (
            <h1 className="text-2xl md:text-3xl font-semibold text-blue-700 mb-2">
              Dudas sobre enfermería y cuidado a domicilio en Guadalajara
            </h1>
          )}
          <div className="inline-flex items-center justify-center p-3 bg-blue-100 rounded-full mb-4">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-600" />
          </div>
          <h2
            id="faq-heading"
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            Preguntas Frecuentes
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Encuentra respuestas a las dudas más comunes sobre nuestros servicios de cuidado y enfermería a domicilio.
          </p>
        </div>

        {/* Búsqueda y filtros */}
        <div className="mb-10 bg-white p-6 rounded-xl shadow-sm">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Buscar en preguntas frecuentes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                aria-label="Buscar en preguntas frecuentes"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  aria-label="Limpiar búsqueda"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={openAll}
                className="px-4 py-3 bg-blue-100 text-blue-700 rounded-lg font-medium hover:bg-blue-200 transition-colors"
              >
                Abrir todo
              </button>
              <button
                onClick={closeAll}
                className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cerrar todo
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div>
              <h3 className="font-medium text-gray-700 mb-2">Filtrar por categoría:</h3>
              <div className="flex flex-wrap gap-2">
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                      activeCategory === category.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-pressed={activeCategory === category.id}
                  >
                    <span>{category.name}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      activeCategory === category.id
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {category.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-medium text-gray-700 mb-2">Tags populares:</h3>
              <div className="flex flex-wrap gap-2">
                {POPULAR_TAGS.map(tag => (
                  <button
                    key={tag}
                    onClick={() => setActiveTag(activeTag === tag ? '' : tag)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      activeTag === tag
                        ? 'bg-blue-700 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-pressed={activeTag === tag}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {(searchTerm || activeCategory !== 'todos' || activeTag) && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Mostrando {filteredFaqs.length} de {faqsToUse.length} preguntas
                {searchTerm && ` para "${searchTerm}"`}
                {activeCategory !== 'todos' && ` en ${categories.find(c => c.id === activeCategory)?.name}`}
                {activeTag && ` con #${activeTag}`}
              </p>
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
              >
                <XMarkIcon className="h-4 w-4" />
                Limpiar filtros
              </button>
            </div>
          )}
        </div>

        {/* Lista de FAQs */}
        <div className="space-y-4">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq) => (
              <article 
                key={faq.id}
                className="bg-white rounded-xl shadow-sm transition-all duration-200 hover:shadow-md border border-gray-100"
              >
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  onKeyDown={(e) => handleKeyDown(e, faq.id)}
                  aria-expanded={openItems[faq.id] || false}
                  aria-controls={`faq-content-${faq.id}`}
                  className="w-full flex justify-between items-start p-6 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 rounded-xl"
                >
                  <div className="flex items-start">
                    <div className="bg-blue-100 p-2 rounded-lg mr-4 mt-1">
                      <QuestionMarkCircleIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">
                        {highlightText(faq.question, searchTerm)}
                      </h3>
                      {faq.tags && faq.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {faq.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <span className="ml-4 flex-shrink-0 mt-1">
                    {openItems[faq.id] ? (
                      <ChevronUpIcon className="h-6 w-6 text-blue-600" />
                    ) : (
                      <ChevronDownIcon className="h-6 w-6 text-blue-600" />
                    )}
                  </span>
                </button>
                
                <div
                  id={`faq-content-${faq.id}`}
                  role="region"
                  aria-labelledby={`faq-question-${faq.id}`}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openItems[faq.id] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-6 pt-2 text-gray-600 leading-relaxed">
                    <div className="pl-11">
                      {highlightText(faq.answer, searchTerm)}
                    </div>
                  </div>
                </div>
              </article>
            ))
          ) : (
            <div className="text-center py-10 bg-white rounded-xl shadow-sm">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No se encontraron resultados</h3>
              <p className="mt-1 text-gray-500">
                No hay preguntas que coincidan con "{searchTerm}"{activeCategory !== 'todos' ? ` en la categoría ${categories.find(c => c.id === activeCategory)?.name}` : ''}{activeTag ? ` con el tag #${activeTag}` : ''}.
              </p>
              <button
                onClick={clearFilters}
                className="mt-4 text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center gap-1 mx-auto"
              >
                <XMarkIcon className="h-4 w-4" />
                Limpiar filtros
              </button>
            </div>
          )}
        </div>

        {/* Sección de contacto adicional */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">¿No encontraste tu respuesta?</h3>
          <p className="mb-6 text-blue-100 max-w-2xl mx-auto">
            Nuestro equipo está disponible para resolver cualquier duda que tengas sobre nuestros servicios.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/523325456522"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              Contactar por WhatsApp
            </a>
            <a
              href="tel:+523325456522"
              className="bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Llamarnos ahora
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FAQs;